import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { env } from '@/app/config/env.config';
import { getCurrentAccessToken } from '@/shared/api/api-client';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notifications.service';
import type {
  AppNotification,
  NotificationFilter,
} from '../models/notification.types';

interface UseNotificationsResult {
  items: AppNotification[];
  unread: number;
  isLoading: boolean;
  filter: NotificationFilter;
  setFilter: (filter: NotificationFilter) => void;
  reload: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

function socketOrigin(): string {
  try {
    return new URL(env.apiBaseUrl).origin;
  } catch {
    return env.apiBaseUrl;
  }
}

/**
 * Loads notifications over REST (server-side filtered via `?filter=`) and
 * subscribes to the Socket.IO `/notifications` namespace for live push. The
 * active `filter` (all | unread | read) refetches from its endpoint; the bell's
 * `unread` total stays accurate regardless of which tab is shown.
 */
export function useNotifications(): UseNotificationsResult {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const socketRef = useRef<Socket | null>(null);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const load = useCallback(async (next: NotificationFilter) => {
    setIsLoading(true);
    try {
      const list = await getNotifications(next);
      setItems(list.items);
      setUnread(list.unread);
    } catch {
      /* keep last-known state on a transient error */
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reload = useCallback(() => load(filterRef.current), [load]);

  // Refetch whenever the active filter changes (tab switch).
  useEffect(() => {
    void load(filter);
  }, [load, filter]);

  useEffect(() => {
    const token = getCurrentAccessToken();
    if (!token) return;
    const socket = io(`${socketOrigin()}/notifications`, {
      auth: { token },
      transports: ['websocket'],
      autoConnect: true,
    });
    socketRef.current = socket;
    socket.on('notification', (n: AppNotification) => {
      setUnread((c) => c + 1);
      // A pushed notification is unread — only surface it on All/Unread tabs.
      if (filterRef.current !== 'read') {
        setItems((cur) => [n, ...cur].slice(0, 50));
      }
    });
    return () => {
      socket.off('notification');
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const markRead = useCallback(async (id: string) => {
    const target = itemsRef.current.find((n) => n.id === id);
    if (target && !target.read) {
      setUnread((c) => Math.max(0, c - 1));
    }
    setItems((cur) =>
      filterRef.current === 'unread'
        ? cur.filter((n) => n.id !== id)
        : cur.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    try {
      await markNotificationRead(id);
    } catch {
      /* optimistic; reconciled on next reload */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setUnread(0);
    setItems((cur) =>
      filterRef.current === 'unread' ? [] : cur.map((n) => ({ ...n, read: true })),
    );
    try {
      await markAllNotificationsRead();
    } catch {
      /* optimistic */
    }
  }, []);

  return { items, unread, isLoading, filter, setFilter, reload, markRead, markAllRead };
}
